import React, { useState } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import { convertPrice } from '../../utils/currency';
import { DLR_GAMES, extrasTotal } from '../../utils/dlrGames';

const perMonth = (t) => parseFloat(t.price) / (t.duration_days / 30);

// Colour sets: "site" is the dark public pricing page, "app" follows the in-app light/dark theme.
const THEMES = {
  site: {
    card: 'border-white/10 bg-white/[0.03]',
    featured: 'border-primary-500/60 bg-primary-600/10',
    title: 'text-white', muted: 'text-gray-400', label: 'text-gray-500', body: 'text-gray-300',
    chip: 'border border-white/10 text-gray-300 hover:border-white/30',
    chipOn: 'bg-white text-slate-900',
    gameOn: 'bg-primary-600 text-white',
    secondary: 'border border-white/20 text-white hover:bg-white/10',
    link: 'text-primary-300 hover:text-primary-200',
  },
  app: {
    card: 'border-gray-200 bg-white shadow-sm dark:border-gray-700/70 dark:bg-gray-800/60',
    featured: 'border-primary-400 bg-primary-50/60 shadow-sm dark:border-primary-500/60 dark:bg-primary-900/20',
    title: 'text-gray-900 dark:text-white', muted: 'text-gray-500 dark:text-gray-400', label: 'text-gray-400 dark:text-gray-500', body: 'text-gray-700 dark:text-gray-300',
    chip: 'border border-gray-200 text-gray-600 hover:border-gray-400 dark:border-gray-600 dark:text-gray-300 dark:hover:border-gray-400',
    chipOn: 'bg-gray-900 text-white dark:bg-white dark:text-gray-900',
    gameOn: 'bg-primary-600 text-white',
    secondary: 'border border-gray-300 text-gray-900 hover:bg-gray-50 dark:border-gray-600 dark:text-white dark:hover:bg-gray-700',
    link: 'text-primary-600 hover:text-primary-500 dark:text-primary-400',
  },
};

// One programme: pick a duration (and DLR games), see one price, act.
// onAction(tier, games) is called with the chosen plan; owned shows the access line instead.
const PlanCard = ({
  title, blurb, bullets = [], tiers, currency, featured = false, badge,
  extras = false, theme = 'site', actionLabel = 'Get started', onAction, owned, onViewSubjects,
}) => {
  const c = THEMES[theme];
  const sorted = [...(tiers || [])].filter((t) => t.is_active !== false).sort((a, b) => a.duration_days - b.duration_days);
  const defaultTier = sorted.find((t) => t.duration_days >= 28 && t.duration_days <= 31) || sorted[0];
  const [selectedId, setSelectedId] = useState(defaultTier?.id);
  const [games, setGames] = useState({});
  const tier = sorted.find((t) => t.id === selectedId) || defaultTier;
  if (!tier) return null;

  const baseline = sorted.find((t) => t.duration_days >= 28);
  const saving = baseline && tier.duration_days > baseline.duration_days
    ? Math.round((1 - perMonth(tier) / perMonth(baseline)) * 100) : 0;
  const gamesPrice = extras ? extrasTotal(games) : 0;

  return (
    <div className={`flex flex-col rounded-2xl border p-6 ${featured ? c.featured : c.card}`}>
      <div className="flex items-start justify-between gap-3">
        <h2 className={`text-lg font-semibold ${c.title}`}>{title}</h2>
        {badge && <span className="whitespace-nowrap rounded-full bg-primary-600 px-2.5 py-0.5 text-xs font-semibold text-white">{badge}</span>}
      </div>
      <p className={`mt-1 text-sm ${c.muted}`}>{blurb}</p>

      {!owned && (<>
      <div className="mt-5 flex flex-wrap gap-1.5" role="radiogroup" aria-label={`${title} duration`}>
        {sorted.map((t) => (
          <button
            key={t.id}
            role="radio"
            aria-checked={t.id === tier.id}
            onClick={() => setSelectedId(t.id)}
            className={`rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors ${t.id === tier.id ? c.chipOn : c.chip}`}
          >
            {t.name}
          </button>
        ))}
      </div>

      {extras && (
        <div className="mt-5">
          <p className={`text-xs font-medium uppercase tracking-wider ${c.label}`}>Add screening games</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {DLR_GAMES.map((g) => (
              <button
                key={g.code}
                onClick={() => setGames((cur) => ({ ...cur, [g.code]: !cur[g.code] }))}
                aria-pressed={!!games[g.code]}
                title={g.name}
                className={`rounded-md px-2 py-1 text-xs font-medium transition-colors ${games[g.code] ? c.gameOn : c.chip}`}
              >
                {g.code} <span className="opacity-75">+{convertPrice(g.price, currency)}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <p className={`mt-5 text-4xl font-semibold tracking-tight ${c.title}`}>{convertPrice(parseFloat(tier.price) + gamesPrice, currency)}</p>
      <p className={`mt-1 min-h-[1.25rem] text-sm ${c.muted}`}>
        {gamesPrice > 0
          ? `Plan ${convertPrice(tier.price, currency)} + games ${convertPrice(gamesPrice, currency)}`
          : (tier.duration_days >= 28 ? `${convertPrice(perMonth(tier), currency)} / month` : `${tier.duration_days} days of access`)}
        {saving > 0 && gamesPrice === 0 && <span className="ml-2 font-medium text-emerald-500">Save {saving}%</span>}
      </p>

      </>)}

      {bullets.length > 0 && !owned && (
        <ul className={`mt-6 space-y-2.5 text-sm ${c.body}`}>
          {bullets.map((b) => (
            <li key={b} className="flex gap-2.5"><CheckIcon className="h-5 w-5 flex-shrink-0 text-emerald-500" />{b}</li>
          ))}
        </ul>
      )}

      <div className="mt-auto pt-6">
        {owned ? (
          <div className="rounded-lg bg-emerald-500/10 px-4 py-3 text-center text-sm font-medium text-emerald-700 dark:text-emerald-300">{owned}</div>
        ) : (
          <button
            onClick={() => onAction?.(tier, extras ? games : {})}
            className={`w-full rounded-lg py-3 text-sm font-semibold transition-colors ${featured ? 'bg-primary-600 text-white hover:bg-primary-500' : c.secondary}`}
          >
            {actionLabel}
          </button>
        )}
        {onViewSubjects && (
          <button onClick={onViewSubjects} className={`mt-3 w-full text-center text-sm font-medium ${c.link}`}>View subjects</button>
        )}
      </div>
    </div>
  );
};

export default PlanCard;
